import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Testimonials from '../content/Testimonials';
import CateringFaq from '../faqs/CateringFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

const featuredTrucks = { featured: true };

function FundraiserCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div className='CateringPage'>
      <Meta
        title={`Food ${vehicle_type} Fundraiser`}
        type='website'
        canonical='/food-truck-catering/fundraiser'
        image='https://static.seattlefoodtruck.com/photos/community.webp'
        description={`Host a food ${vehicleType} fundraiser in ${city_name}. Schools, charities & nonprofits — earn a percentage of proceeds with zero upfront cost. Learn how it works.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Fundraiser in ${city_name}`,
            description: `Host a food ${vehicleType} fundraiser in ${city_name}. Schools, charities and nonprofits earn a percentage of proceeds with zero upfront cost.`,
            url: '/food-truck-catering/fundraiser',
            serviceType: 'Food Truck Fundraiser',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Fundraiser', item: '/food-truck-catering/fundraiser' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/community.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Host a Food {vehicle_type} Fundraiser</h1>
            <p>Looking for a fun, easy way to raise money for your school, charity, or organization? A food {vehicleType} fundraiser draws a crowd, keeps everyone fed, and earns your group a percentage of proceeds — often with zero upfront cost. {site_title} connects you with {city_name}&#39;s best food {vehicleType}s that have experience running successful fundraiser events.</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Plan Your Fundraiser</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <FeaturedTrucks title={`Popular Food ${vehicle_type}s for Fundraisers`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Host a Food ${vehicle_type} Fundraiser?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>How Food {vehicle_type} Fundraisers Work</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>The Percentage-of-Proceeds Model</h3>
            <p>Most food {vehicleType} fundraisers work on a simple profit-sharing model. The {vehicleType} shows up at your event, sells food to attendees, and donates an agreed-upon percentage of sales back to your organization. There&#39;s typically no upfront cost to you — the {vehicleType} handles the food, the cooking, and the cleanup. All you need to do is promote the event, bring the crowd, and watch the funds roll in.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>School & Charity Fundraising</h3>
            <p>Food {vehicleType} fundraisers are a favorite for schools, PTAs, sports teams, churches, and nonprofits across {city_name}. They&#39;re easy to organize, appeal to all ages, and can be combined with other activities like silent auctions, bake sales, or field days. Book one {vehicleType} for a small event or several for a full-scale festival — the more variety you offer, the more sales you&#39;ll generate.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Tips for a Successful Fundraiser</h3>
            <p>Maximize your fundraiser&#39;s success by promoting the event early on social media, flyers, and email lists. Choose a high-traffic location with plenty of parking and foot traffic. Schedule during peak meal times — lunch and dinner events tend to generate the most sales. Consider inviting multiple {vehicleType}s to offer variety and keep wait times short. And don&#39;t forget to share the cause — people are more likely to show up and spend when they know their purchase supports a good mission.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title="See what our customers are saying" />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={`Plan a Food ${vehicle_type} Fundraiser Today!`} />
      </section>
    </div>
  );
}

export default FundraiserCatering;
